import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonButton, IonIcon, IonInput, IonItem, IonSpinner } from '@ionic/react';
import { chevronDownOutline, close, pricetagOutline } from 'ionicons/icons';

import { useDispatch, useSelector } from 'src/store';
import { applyVoucher, clearVoucherError, removeVoucher } from 'src/slices/cart';
import { useCartPriceGetter } from 'src/hooks';
import classes from './VoucherInput.module.scss';

interface VoucherInputProps {
  onKeyboardChange?: (open: boolean) => void;
}

const VoucherInput: React.FC<VoucherInputProps> = ({ onKeyboardChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [expanded, setExpanded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const { appliedVoucher, voucherValidation } = useSelector((state) => state.cart);
  const { loading, error } = voucherValidation;
  const { voucherDiscount } = useCartPriceGetter();

  const handleFocus = () => {
    if (!onKeyboardChange) return;
    onKeyboardChange(true);
    setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const ionContent = el.closest('ion-content') as HTMLIonContentElement | null;
      if (!ionContent) return;
      ionContent.getScrollElement().then((scrollEl) => {
        const elRect = el.getBoundingClientRect();
        const scrollRect = scrollEl.getBoundingClientRect();
        const y = elRect.top - scrollRect.top + scrollEl.scrollTop;
        ionContent.scrollToPoint(0, y - 100, 300);
      });
    }, 300);
  };

  const handleBlur = () => {
    if (!onKeyboardChange) return;
    setTimeout(() => {
      onKeyboardChange(false);
    }, 100);
  };

  const handleApplyVoucher = () => {
    const trimmedCode = voucherCode?.trim();
    if (!trimmedCode) return;
    dispatch(applyVoucher(trimmedCode.toUpperCase()));
  };

  const trimmedCode = voucherCode?.trim() || '';
  const hasValidInput = trimmedCode.length > 0;
  const isButtonDisabled = !hasValidInput || loading;

  const handleInputChange = (e: any) => {
    const value = e.target?.value || e.detail?.value || '';
    setVoucherCode(value);
    if (error) {
      dispatch(clearVoucherError());
    }
  };

  // TotalPrice shows remove button when voucherDiscount > 0; show here only for
  // free_product/conditional_free vouchers where discount is 0
  if (appliedVoucher && voucherDiscount > 0) return null;

  if (appliedVoucher) {
    return (
      <div className={classes.appliedRow}>
        <IonIcon icon={pricetagOutline} className={classes.toggleIcon} />
        <span className={classes.appliedCode}>{appliedVoucher.code}</span>
        <IonButton fill="clear" size="small" onClick={() => dispatch(removeVoucher())} className={classes.removeBtn}>
          <IonIcon icon={close} className={classes.removeIcon} />
        </IonButton>
      </div>
    );
  }

  if (!expanded) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={classes.toggleRow}
        onClick={() => setExpanded(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded(true);
        }}
      >
        <IonIcon icon={pricetagOutline} className={classes.toggleIcon} />
        <span className={classes.toggleText}>{t('Have a voucher code?')}</span>
        <IonIcon icon={chevronDownOutline} className={classes.chevronIcon} />
      </div>
    );
  }

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.inputRow}>
        <div className={classes.inputContainer}>
          <IonItem className={classes.inputItem}>
            <IonInput
              value={voucherCode}
              placeholder={t('Enter voucher code')}
              onIonInput={handleInputChange}
              onIonFocus={handleFocus}
              onIonBlur={handleBlur}
              className={classes.input}
              disabled={loading}
              autocapitalize="characters"
              spellcheck={false}
            />
          </IonItem>
        </div>
        <IonButton
          fill="outline"
          color="primary"
          onClick={handleApplyVoucher}
          disabled={isButtonDisabled}
          className={classes.applyButton}
        >
          {loading ? <IonSpinner name="crescent" className={classes.spinner} /> : t('Apply')}
        </IonButton>
      </div>
      {error && <span className={classes.errorText}>{error}</span>}
    </div>
  );
};

export default VoucherInput;
